/**
 * Wires the "You're not being heard" rules (lib/notHeard.ts) to a live room
 * (#2224 D5). `VoicePathMonitor` creates one per call and calls `observe`
 * after each 1 s packet sample; everything the user sees goes through
 * `useNotHeardStore`.
 *
 * It owns the side effects the pure rules only decide on:
 *  - reads the staging call monitor's `weco.monitor` signal (accepted only
 *    from the monitor participant) for agreement mode;
 *  - runs the recovery ladder: restart the mic (the same routine the
 *    device-interruption recovery uses), then a full rejoin through the
 *    registered reconnect entry point, via lib/rejoinGate.ts so it never
 *    rejoins right after the listener's "can't hear" ladder did (#2223);
 *  - broadcasts this speaker's own warning on `weco.not-heard` so others can
 *    mark the tile, and collects everyone else's;
 *  - writes one `[weco-not-heard]` console line per shown / cleared warning
 *    and per recovery step, which is what staging verification reads.
 */
import { RoomEvent, type Participant } from "livekit-client";
import {
  decodeMonitorSignal,
  decodeNotHeardSignal,
  encodeNotHeardSignal,
  NotHeardDetector,
  notHeardLogLine,
  NOT_HEARD_TOPIC,
  RecoveryLadder,
  rejoinWarranted,
  shouldSendNotHeard,
  SpeakerSignals,
  tileMarks,
  type LadderAction,
  type LadderStep,
  type MonitorSignal,
  type NotHeardLogEvent,
  type OutboundVerdict,
} from "./notHeard";
import { CALL_MONITOR_TOPIC, isCallMonitor } from "./callMonitor";
import { useNotHeardStore } from "../store/notHeardStore";
import { emitCallActivity } from "./callActivity";
import { requestGatedRejoin } from "./rejoinGate";

interface ParticipantLike {
  identity: string;
  isAgent?: boolean;
}

/** What the runner needs of a LiveKit room (a subset, so tests can fake it). */
export interface NotHeardRoomLike {
  localParticipant: {
    identity: string;
    publishData(data: Uint8Array, options: { reliable?: boolean; topic?: string }): Promise<void>;
  };
  remoteParticipants: Map<string, ParticipantLike>;
  on(event: RoomEvent, listener: (...args: never[]) => void): unknown;
  off(event: RoomEvent, listener: (...args: never[]) => void): unknown;
}

export interface NotHeardRunnerDeps {
  /** Step 1: restart the local mic track. */
  restartMic: () => Promise<unknown>;
  /** The server's call-diagnostics flag, read fresh each sample. */
  diagnostics: () => boolean;
  log?: (line: string) => void;
  now?: () => number;
}

export interface NotHeardObservation {
  at: number;
  connected: boolean;
  unmuted: boolean;
  outbound: OutboundVerdict;
  /** Open inbound outages here: speaker -> since when (OutageTracker.missingSince). */
  missingSince: Map<string, number>;
}

export interface NotHeardRunner {
  observe(o: NotHeardObservation): void;
  dispose(): void;
}

export function createNotHeardRunner(room: NotHeardRoomLike, deps: NotHeardRunnerDeps): NotHeardRunner {
  const log = deps.log ?? ((line: string) => console.info(line));
  const now = deps.now ?? (() => Date.now());
  const store = useNotHeardStore;
  const detector = new NotHeardDetector();
  const ladder = new RecoveryLadder();
  const signals = new SpeakerSignals();
  let monitor: { at: number; signal: MonitorSignal } | null = null;
  let lastSent: { warning: boolean; at: number } | null = null;
  let lastMissing = new Map<string, number>();
  let disposed = false;
  // The current ladder run was started by "Reconnect audio" (not gated).
  let manual = false;

  const write = (event: NotHeardLogEvent, at: number, detail?: Record<string, string>) =>
    log(notHeardLogLine(event, at, detail));

  const excluded = (): Set<string> => {
    const out = new Set<string>();
    for (const p of room.remoteParticipants.values()) if (p.isAgent || isCallMonitor(p)) out.add(p.identity);
    return out;
  };

  const publishMarks = (at: number) => {
    store.getState().setMarks(
      tileMarks({
        now: at,
        self: room.localParticipant.identity,
        broadcast: signals.active(at),
        missingSince: lastMissing,
        excluded: excluded(),
      }),
    );
  };

  const run = (action: LadderAction | null, at: number) => {
    if (action === "restartMic") {
      write("step 1 restart mic", at);
      emitCallActivity("recovery", { trigger: "not_heard", step: "restart_mic", result: "started" });
      void deps.restartMic().catch((err: unknown) => {
        emitCallActivity("recovery", { trigger: "not_heard", step: "restart_mic", result: "failed" });
        // A DOMException is not always an Error instance (jsdom, older engines): read the name.
        const name = (err as { name?: unknown } | null)?.name;
        write("step 1 restart mic failed", now(), { error: typeof name === "string" && name ? name : "unknown" });
      });
    } else if (action === "rejoin") {
      write("step 2 full rejoin", at);
      // Through the shared gate: the listener's "can't hear" ladder may have
      // rejoined moments ago, and two rejoins back to back help nobody. A
      // press of "Reconnect audio" is the user's call and is not gated.
      const outcome = requestGatedRejoin(at, { force: manual });
      if (outcome === "recent") write("step 2 skipped, rejoined moments ago", at);
      emitCallActivity("recovery", {
        trigger: "not_heard",
        step: "rejoin",
        result: outcome === "started" ? "started" : "failed",
      });
      if (outcome !== "started") ladder.rejoinRefused();
    }
  };

  const afterLadder = (before: LadderStep, at: number) => {
    if (ladder.current === "failed" && before !== "failed") write("recovery failed, offering Reconnect audio", at);
    store.getState().setSelf(detector.isWarning, ladder.current);
  };

  const onData = (payload: Uint8Array, participant?: Participant, _kind?: unknown, topic?: string) => {
    if (!participant) return;
    if (topic === CALL_MONITOR_TOPIC) {
      if (!isCallMonitor(participant)) return;
      const signal = decodeMonitorSignal(payload);
      if (signal) monitor = { at: now(), signal };
      return;
    }
    if (topic === NOT_HEARD_TOPIC) {
      if (participant.isAgent) return;
      const signal = decodeNotHeardSignal(payload);
      if (!signal) return;
      const at = now();
      signals.receive(participant.identity, signal.warning, at);
      publishMarks(at);
    }
  };
  const onDisconnected = () => ladder.noteDisconnected();
  const onParticipantGone = (participant: Participant) => {
    signals.forget(participant.identity);
    publishMarks(now());
  };
  room.on(RoomEvent.DataReceived, onData as never);
  room.on(RoomEvent.Disconnected, onDisconnected as never);
  room.on(RoomEvent.ParticipantDisconnected, onParticipantGone as never);

  // "Reconnect audio": rerun the ladder from step 1.
  const unsubscribe = store.subscribe((s, prev) => {
    if (disposed || s.retryNonce === prev.retryNonce) return;
    const at = now();
    write("Reconnect audio pressed", at);
    const before = ladder.current;
    manual = true;
    run(ladder.retry(at), at);
    afterLadder(before, at);
  });

  return {
    observe(o) {
      if (disposed) return;
      const self = room.localParticipant.identity;
      const state = detector.observe({
        at: o.at,
        connected: o.connected,
        unmuted: o.unmuted,
        outbound: o.outbound,
        diagnostics: deps.diagnostics(),
        monitor: monitor ? { at: monitor.at, track: monitor.signal.tracks[self] ?? null } : null,
      });
      if (state.changed) {
        write(state.warning ? "warning shown" : "warning cleared", o.at, {
          mode: state.mode,
          ...(state.warning && state.reason ? { reason: state.reason } : {}),
        });
      }
      const before = ladder.current;
      if (!state.warning) manual = false;
      run(
        ladder.update({
          at: o.at,
          warning: state.warning,
          connected: o.connected,
          canRejoin: !!store.getState().rejoin,
          rejoinWarranted: rejoinWarranted(state),
        }),
        o.at,
      );
      afterLadder(before, o.at);

      if (o.connected && shouldSendNotHeard(state.warning, lastSent, o.at)) {
        const sent = { warning: state.warning, at: o.at };
        lastSent = sent;
        room.localParticipant
          .publishData(encodeNotHeardSignal(state.warning), { reliable: true, topic: NOT_HEARD_TOPIC })
          .catch(() => {
            // Lost: let the next sample try again.
            if (lastSent === sent) lastSent = sent.warning ? null : { warning: true, at: 0 };
          });
      }

      lastMissing = o.missingSince;
      publishMarks(o.at);
    },
    dispose() {
      disposed = true;
      unsubscribe();
      room.off(RoomEvent.DataReceived, onData as never);
      room.off(RoomEvent.Disconnected, onDisconnected as never);
      room.off(RoomEvent.ParticipantDisconnected, onParticipantGone as never);
      store.getState().reset();
    },
  };
}
